import { AxiosResponse } from "axios";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import * as actions from "./actions";
import * as actionTypes from "./actionTypes";
import * as types from "./types";
import * as services from "./service";
import * as alert from "../alert/actions";

/*
  Worker Sagas
*/
function* fetchAccessControlSaga() {
    try {
        const response: AxiosResponse<types.IAccessControl[]> = yield call(services.getAccessControls);

        yield put(
            actions.fetchAccessControlSuccess({
                accessControls: response.data,
            })
        );
    } catch (e: any) {
        yield put(
            actions.fetchAccessControlFailure({
                error: e.message,
            })
        );
    }
}

function* fetchSourceListSaga(action: types.FetchSourcesReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IDataSourceList | types.IApiError> = yield call(services.getDataSources, action.params);

        if (response.status === 200) {
            yield put(
                actions.fetchSourcesSuccess({
                    sources: response.data as types.IDataSourceList,
                })
            );
        }

        //Show IApiError
        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }

    } catch (e: any) {
        yield put(
            actions.fetchSourcesFailure({
                error: e,
            })
        );
    }
}

function* fetchSourceDetailsSaga(action: types.FetchSourceReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IDataSourceDetails | types.IApiError> = yield call(services.getSourceDetails, action.params);

        if (response.status === 200) {
            yield put(
                actions.fetchSourceDetailsSuccess({
                    source: response.data as types.IDataSourceDetails,
                })
            );
        }

        //Show IApiError
        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }

    } catch (e: any) {
        yield put(
            actions.fetchSourceDetailsFailure({
                error: e,
            })
        );
    }
}

function* deleteSourceSaga(action: types.DeleteDataSourceRequest) {
    const timestamp = new Date().toLocaleString();

    try {
        const response: AxiosResponse<types.IDeleteDataSourceSuccess | types.IApiError> = yield call(services.deleteSource, action.params);

        if (response.status === 204) {
            yield put(
                actions.deleteSourceSuccess({
                    success: true
                })
            );
            yield put(alert.enqueueSnackbar({ message: `The data source was successfully deleted. Timestamp: ${timestamp}`, options: { variant: "success" } }));

        }

        //Show IApiError
        if (response.status !== 204) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
            yield put(
                actions.deleteSourceFailure({
                    error: apiError
                })
            );
        }

    } catch (e: any) {
        yield put(
            actions.deleteSourceFailure({
                error: e,
            })
        );
    }
}

function* postDataSourceSaga({ payload }: types.PostDataSourceRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPostDataSourceSuccess | types.IApiError> = yield call(services.postDataSource, payload);

        if (response.status === 201) {
            let dataPayload = response.data as types.IPostDataSourceSuccess;
            yield put(
                actions.postDataSourceSuccess({
                    dataSource: dataPayload,
                })
            );
            let successMsg = `The data source was successfully created: ${dataPayload.sourceName}, source ID: ${dataPayload.sourceId}.Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 201) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));
        }
    } catch (e: any) {
        yield put(
            actions.postDataSourceFailure({
                error: e,
            })
        );
    }
}



function* putDataSourceSaga({ sourceId, payload }: types.PutDataSourceRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPutDataSourceSuccess | types.IApiError> = yield call(services.putDataSource, sourceId, payload);

        if (response.status === 200) {
            let dataPayload = response.data as types.IPutDataSourceSuccess;
            yield put(
                actions.putDataSourceSuccess({
                    dataSource: dataPayload,
                })
            );
            let successMsg = `The data source was successfully edited. Source ID: ${sourceId}.Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));
        }
    } catch (e: any) {
        yield put(
            actions.putDataSourceFailure({
                error: e,
            })
        );
    }
}



function* postConnectionSaga({ payload }: types.PostConnectionRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPostConnectionSuccess | types.IApiError> = yield call(services.postConnection, payload);

        if (response.status === 200) {
            let connPayload = response.data as types.IPostConnectionSuccess;
            yield put(
                actions.postConnectionSuccess({
                    succeeded: connPayload,
                })
            );
            let successMsg = `The connection was successful.Time stamp:${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(
                actions.postConnectionFailure({
                    error: apiError,
                })
            );
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));
        }
    } catch (e: any) {
        let apiError = e as types.IApiError;
        let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
        yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));

        yield put(
            actions.postConnectionFailure({
                error: apiError,
            })
        );
    }
}

function* fetchStewardSaga(action: types.IFetchStewardRequest) {
    try {
        const response: AxiosResponse<types.ISteward[]> = yield call(services.getStewards, action.params);

        yield put(
            actions.fetchStewardSuccess({
                userName: response.data,
            })
        );
    } catch (e: any) {
        yield put(
            actions.fetchStewardFailure({
                error: e.message,
            })
        );
    }
}


/*
  Watcher saga - intercept the action fired from view
  Starts worker saga on latest dispatched `FETCH_ACCESSCONTROL_REQUEST` action.
  Allows concurrent increments.
*/
function* accessControlSaga() {
    yield takeLatest(actionTypes.FETCH_ACCESSCONTROL_REQUEST, fetchAccessControlSaga);
}

function* sourceListSaga() {
    yield takeLatest(actionTypes.FETCH_SOURCES_LIST_REQUEST, fetchSourceListSaga);
}

function* sourceDetailsSaga() {
    yield takeEvery(actionTypes.FETCH_SOURCE_REQUEST, fetchSourceDetailsSaga);
}

function* deleteDataSourceSaga() {
    yield takeEvery(actionTypes.DELETE_SOURCE_REQUEST, deleteSourceSaga);
}

function* createDataSourceSaga() {
    yield takeLatest(actionTypes.POST_DATASOURCE_REQUEST, postDataSourceSaga);
}

function* editDataSourceSaga() {
    yield takeEvery(actionTypes.PUT_DATASOURCE_REQUEST, putDataSourceSaga);
}

function* checkConnectionSaga() {
    yield takeLatest(actionTypes.POST_CONNECTION_REQUEST, postConnectionSaga);
}

function* stewardDataSaga() {
    yield all([
        takeLatest(actionTypes.FETCH_STEWARD_REQUEST, fetchStewardSaga),
    ]);
}




function* dataSourceSaga() {
    yield all([
        accessControlSaga(),
        sourceListSaga(),
        sourceDetailsSaga(),
        createDataSourceSaga(),
        checkConnectionSaga(),
        stewardDataSaga(),
        deleteDataSourceSaga(),
        editDataSourceSaga(),
    ])
};

export default dataSourceSaga;
